import React from 'react';
import { Feature, ColorFrom } from '@tencent/workbench';
import { NumberInputFrom } from '@tencent/wb-ext-form';
import BaseDrawerEffectFeature from '@framework/workbench/common/feature/base-drawer-effect-feature';
import GlowPresetMenu from '@framework/frame-pc/components/palette-panel/effect-preset-menu/glow-preset-menu';
import { FormItemName, INPUT_PROPS_CALLBACK_KEY } from '@framework/workbench/drawer/constants';
import PresetPanel from '@framework/workbench/drawer/components/preset-panel';
import { IMultiplyModel } from '@src/slide/domain/ui-model/element-modeles/Interface/multiply-model.interface';
import {
    IElementBaseModel,
} from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { getDrawerColorOptions } from '@framework/workbench/drawer/utils/color-options';
import { ColorSelectButton } from '@framework/workbench/drawer/components/color-select-button';
import {
    TDWVer5Type,
    TDWVer6Type,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { ITDColor, TDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';
import { alphaToOpacity } from '@framework/workbench/drawer/utils';
import { handleInputBlurInitAndReset } from '@src/slide/application/service/drawer-input-manager/drawer-handler';
import { ActionType } from '@src/framework/editor/src/js/constants/common-action-type';
import { PresetUtilFactory } from '@src/framework/frame-pc/components/palette-panel/effect-preset-menu/preset-util/preset-util-facotry';
import { EMPTY_EFFECT } from '@src/framework/frame-pc/components/palette-panel/effect-preset-menu/preset-util/base-preset-util';
import { PresetElementType } from '@src/framework/workbench/const/preset-element-type';
import { ColorSourceType } from '@src/js/common/ark-extensions/dc-components/color-picker/type';
import { IGlowFeature } from '@tencent/drawing-drawer-components';

const defaultGlowProps = {
    color: '#0E0E0E',
    opacity: 0,
    size: 0,
    tdColor: new TDColor({
        rgbaStr: '#0E0E0E',
    }),
};

const DEFAULT_WHITE_COLOR = '#000000';

@Feature()
export class GlowFeature extends BaseDrawerEffectFeature implements IGlowFeature {
    public glowProps = { ...defaultGlowProps };
    public get glowInfo() {
        return (this.getSelectModel() as IMultiplyModel | IElementBaseModel)?.getEffect?.()
            .getGlow() ?? { ...defaultGlowProps };
    }
    public get isMultiDifferent(): boolean {
        return this.isMultiDiffValue('effect.glow');
    }
    public get opacity() {
        return Math.round(Number(this.glowProps?.opacity));
    }
    public get size() {
        return Number(this.glowProps?.size ?? 0);
    }
    protected previewInfo = this.glowProps;

    public constructor() {
        super();
        this.addInitPropsCallback(INPUT_PROPS_CALLBACK_KEY.GLOW);
    }

    public initPropsCallback = () => {
        this.glowProps = { ...this.glowInfo };
    };

    public renderPreset() {
        const { iconStyle, iconName } = PresetUtilFactory.getPresetUtil(ActionType.GLOW)
            .getPresetStyle(this.glowInfo);
        return (<PresetPanel
            src={iconStyle}
            hasEffect={iconName !== EMPTY_EFFECT}
            isMultiDifferent={this.isMultiDifferent}
            onVisibleChange={this.handlePresetVisibleChange}
        >
            <GlowPresetMenu id={' '} preset={{ style: iconName }}
                presetElementType={PresetElementType.SHAPE}
                themeColors={this.getThemeColorManager()?.currentThemeModel?.clrScheme}
                onGlowPresetStyleClick={glow => this.handleGlowPresetStyleClick(glow)}
                onGlowPresetStyleMouseEnter={glow => this.handleGlowPresetSytleEnter(glow)}
                onGlowPresetStyleMouseLeave={() => this.handleGlowPresetStyleMouseLeave()} />
        </PresetPanel>);
    }

    public updateGlowProps = (onlyChangeUI?: boolean, onPreview?: boolean) => {
        if (this.checkSelectType() <= 1) {
            this.getAppService().editableServiceLayer?.previewService?.updateElementPreviewState(false);
        }
        const glow = { ...onPreview ? this.previewInfo : this.glowProps };
        (this.getSelectModel() as IMultiplyModel | IElementBaseModel)?.getEffect?.()
            .updateGlow(glow, !!onlyChangeUI);
    };

    @handleInputBlurInitAndReset(INPUT_PROPS_CALLBACK_KEY.GLOW)
    public handleGlowChange(value, dragEnd?: boolean, from?: NumberInputFrom, max?: number) {
        const glowObj = this.getValidValue(value, from, max);
        const key = value && Object.keys(value)[0];
        if (key === 'opacity') {
            this.glowInfo.tdColor?.updateOpacity?.(value?.opacity);
        }
        this.glowProps = { ...this.glowInfo, ...glowObj };
        const onlyChangeUI = this.isInputOnlyChangeUIWithDrag(dragEnd);
        this.updateGlowProps(onlyChangeUI);
        this.handleGlowReport(glowObj, from);
    }

    /**
     * 从‘预设’面板切换不同的阴影类型
     * @param {object} value 阴影的属性
     */
    public handleGlowPresetStyleClick = (glow) => {
        this.getAppService().editableServiceLayer?.previewService?.updateElementPreviewState(false);
        this.glowProps = { ...defaultGlowProps, ...glow };
        this.updateGlowProps();
        this.tdwDrawerReport(FormItemName.GLOW, { ver5: glow ? 2 : 1 });
    };

    public handleGlowPresetSytleEnter = (glow) => {
        if (this.checkSelectType() > 1) {
            return;
        }
        (this.getSelectModel() as IMultiplyModel | IElementBaseModel)?.getEffect?.()
            .updateGlow(glow, true);
        this.getAppService().editableServiceLayer?.previewService?.updateElementPreviewState(true);
    };

    public handleGlowPresetStyleMouseLeave = () => {
        if (this.checkSelectType() > 1) {
            return;
        }
        const glow = { ...this.glowProps };
        (this.getSelectModel() as IMultiplyModel | IElementBaseModel)?.getEffect?.()
            .updateGlow(glow, true);
        this.getAppService().editableServiceLayer?.previewService?.updateElementPreviewState(false);
    };

    public handleColorChange = (options) => {
        this.glowProps = { ...this.glowInfo, ...options };
        this.updateGlowProps();
    };

    public handlePresetVisibleChange = (visible: boolean) => {
        if (visible) {
            this.glowProps = { ...this.glowInfo };
        }
    };

    public handleSelectColorChange = (color: string, from: ColorFrom) => {
        this.handleGlowColorChange(new TDColor({ rgbaStr: color }), from);
    };

    public updatePreviewInfo = () => {
        this.previewInfo = { ...this.glowProps };
    };

    public handleGlowColorChange = (tdColor: ITDColor, from: ColorFrom | ColorSourceType) => {
        const options = {
            color: tdColor.srgb_color,
            tdColor,
            opacity: alphaToOpacity(tdColor.alpha),
        };
        this.handleColorChange(options);
        this.getColorPickerManager()?.colorPickerTdwReport({
            color: tdColor.srgb_color,
            colorPickerFrom: TDWVer6Type.COLOR_PICKER_DRAWER,
            colorGroupFrom: from,
            actionType: TDWVer5Type.COLOR_PICKER_GLOW,
        });
    };

    // hover 到颜色上时预览属性
    public handleColorEnter = (color) => {
        if (this.checkSelectType() > 1) {
            return;
        }
        this.previewInfo = { ...this.glowProps, color };
        this.updateGlowProps(true, true);
        this.getAppService().editableServiceLayer?.previewService?.updateElementPreviewState(true);
    };

    public handlePaletteLeave = () => {
        if (this.checkSelectType() > 1) {
            return;
        }
        this.previewInfo = { ...this.glowProps };
        this.updateGlowProps(true);
        this.getAppService().editableServiceLayer?.previewService?.updateElementPreviewState(false);
    };

    public getColorSelectButton() {
        return <ColorSelectButton
            onColorHover={this.handleColorEnter}
            onMouseLeave={this.handlePaletteLeave}
            onColorChange={this.handleGlowColorChange}
            value={this.glowInfo?.tdColor || new TDColor({
                rgbaStr: DEFAULT_WHITE_COLOR,
            })}
            paletteButtonsStyle={{
                marginLeft: 'auto',
                boxSizing: 'border-box',
            }}
        />;
    }

    private handleGlowReport = (value, from?: NumberInputFrom) => {
        let reportId;
        let ver6;
        if (value?.size) {
            reportId  = FormItemName.SHADOW_SIZE;
            ver6 = value.size;
        }
        if (value?.opacity) {
            reportId  = FormItemName.SHADOW_OPICITY;
            ver6 = value.opacity;
        }
        this.tdwDrawerReportNumberInput(reportId, { ver6 }, from);
    };

    private getThemeColorManager = () => this.getAppServices().editableServiceLayer?.themeColorManager;
}
